import { useEffect, useState } from "react";

/* ─── Dark mode detection (watches Tailwind's `dark` class on <html>) ─── */
export const useDark = () => {
  const [dark, setDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => {
      setDark(el.classList.contains("dark"));
    });
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

/* ─── Theme tokens — matches the feed control bar exactly ─── */
export const getTheme = (dark) =>
  dark
    ? {
        // Surface
        barBg: "rgba(11,15,20,0.95)",
        barBorder: "rgba(14,165,233,0.15)",
        barShadow: "0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(14,165,233,0.2)",
        divider: "rgba(14,165,233,0.15)",
        // Inputs & buttons
        inputBg: "rgba(17,24,39,0.60)",
        inputBorder: "rgba(14,165,233,0.12)",
        inputText: "rgba(226,232,240,0.9)",
        inputPlaceholder: "rgba(148,163,184,0.5)",
        pillBg: "rgba(17,24,39,0.60)",
        pillBorder: "rgba(14,165,233,0.12)",
        pillText: "rgba(148,163,184,0.6)",
        pillIconColor: "rgba(148,163,184,0.50)",
        // Active states
        activeBg: "rgba(14,165,233,0.15)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeText: "rgba(14,165,233,0.9)",
        activeIcon: "rgba(14,165,233,0.85)",
        // Hover
        hoverBg: "rgba(17,24,39,0.80)",
        hoverBorder: "rgba(56,189,248,0.25)",
        hoverShadow: "0 0 8px rgba(28,157,234,0.12)",
        hoverBtnShadow: "0 0 12px rgba(28,157,234,0.15)",
        // Text
        textPrimary: "rgba(226,232,240,0.9)",
        textSecondary: "rgba(148,163,184,0.7)",
        textMuted: "rgba(148,163,184,0.4)",
        // Dropdown
        dropBg: "rgba(11,15,20,0.95)",
        dropBorder: "rgba(14,165,233,0.15)",
        dropShadow: "0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(14,165,233,0.2)",
        dropHover: "rgba(14,165,233,0.06)",
        // Icon btn default
        iconDefault: "rgba(148,163,184,0.60)",
        // Clear / danger
        clearText: "rgba(248,113,113,0.9)",
        clearBg: "rgba(239,68,68,0.10)",
        clearBgHover: "rgba(239,68,68,0.20)",
        clearBorder: "rgba(239,68,68,0.20)",
        // Success / enable
        successText: "rgba(74,222,128,0.9)",
        successBg: "rgba(34,197,94,0.10)",
        successBgHover: "rgba(34,197,94,0.20)",
        successBorder: "rgba(34,197,94,0.20)",
        // Toggle
        toggleBg: "rgba(14,165,233,0.9)",
        toggleBgOff: "rgba(100,116,139,0.4)",
        toggleKnob: "#ffffff",
        // Status dot
        dotActive: "rgba(14,165,233,1)",
        dotActiveShadow: "0 0 6px rgba(14,165,233,0.5)",
        dotInactive: "rgba(100,116,139,0.5)",
      }
    : {
        // Surface
        barBg: "rgba(255,255,255,0.92)",
        barBorder: "rgba(203,213,225,0.5)",
        barShadow: "0 4px 24px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.05)",
        divider: "rgba(203,213,225,0.4)",
        // Inputs & buttons
        inputBg: "rgba(241,245,249,0.8)",
        inputBorder: "rgba(203,213,225,0.5)",
        inputText: "rgba(15,23,42,0.9)",
        inputPlaceholder: "rgba(100,116,139,0.6)",
        pillBg: "rgba(241,245,249,0.8)",
        pillBorder: "rgba(203,213,225,0.4)",
        pillText: "rgba(100,116,139,0.7)",
        pillIconColor: "rgba(100,116,139,0.50)",
        // Active states
        activeBg: "rgba(14,165,233,0.10)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeText: "rgba(2,132,199,1)",
        activeIcon: "rgba(2,132,199,0.85)",
        // Hover
        hoverBg: "rgba(241,245,249,1)",
        hoverBorder: "rgba(14,165,233,0.25)",
        hoverShadow: "0 0 8px rgba(14,165,233,0.08)",
        hoverBtnShadow: "0 0 10px rgba(14,165,233,0.10)",
        // Text
        textPrimary: "rgba(15,23,42,0.9)",
        textSecondary: "rgba(71,85,105,0.8)",
        textMuted: "rgba(148,163,184,0.6)",
        // Dropdown
        dropBg: "rgba(255,255,255,0.98)",
        dropBorder: "rgba(203,213,225,0.5)",
        dropShadow: "0 8px 24px rgba(0,0,0,0.10), 0 0 1px rgba(0,0,0,0.05)",
        dropHover: "rgba(241,245,249,0.8)",
        // Icon btn default
        iconDefault: "rgba(100,116,139,0.60)",
        // Clear / danger
        clearText: "rgba(220,38,38,0.9)",
        clearBg: "rgba(239,68,68,0.08)",
        clearBgHover: "rgba(239,68,68,0.15)",
        clearBorder: "rgba(239,68,68,0.15)",
        // Success / enable
        successText: "rgba(22,163,74,0.9)",
        successBg: "rgba(34,197,94,0.08)",
        successBgHover: "rgba(34,197,94,0.15)",
        successBorder: "rgba(34,197,94,0.15)",
        // Toggle
        toggleBg: "rgba(14,165,233,0.9)",
        toggleBgOff: "rgba(203,213,225,0.6)",
        toggleKnob: "#ffffff",
        // Status dot
        dotActive: "rgba(2,132,199,1)",
        dotActiveShadow: "0 0 6px rgba(14,165,233,0.35)",
        dotInactive: "rgba(148,163,184,0.5)",
      };
